'use client'

import * as React from 'react'
import { toast as sonnerToast } from 'sonner'

type ToastVariant = 'default' | 'destructive' | null

type ToastOptions = {
  id?: string | number
  title?: React.ReactNode
  description?: React.ReactNode
  duration?: number
  variant?: ToastVariant
}

type ToastHandle = {
  id: string
  dismiss: () => void
  update: (props: Partial<ToastOptions>) => void
}

function normalizeToastId(id: string | number): string {
  return String(id)
}

function resolveTitleAndDescription(options: ToastOptions): { title: React.ReactNode; description?: React.ReactNode } {
  const hasTitle = options.title !== undefined && options.title !== null && options.title !== ''
  if (hasTitle) {
    return {
      title: options.title as React.ReactNode,
      description: options.description,
    }
  }
  if (options.description !== undefined && options.description !== null && options.description !== '') {
    return { title: options.description }
  }
  return { title: 'Notification' }
}

function emitToast(options: ToastOptions): string {
  const { title, description } = resolveTitleAndDescription(options)
  const payload = {
    id: options.id,
    description,
    duration: options.duration,
  }
  const toastId =
    options.variant === 'destructive'
      ? sonnerToast.error(title, payload)
      : sonnerToast(title, payload)
  return normalizeToastId(toastId)
}

function toast(options: Omit<ToastOptions, 'id'>): ToastHandle {
  const id = emitToast(options)
  return {
    id,
    dismiss: () => sonnerToast.dismiss(id),
    update: (next: Partial<ToastOptions>) => {
      emitToast({
        ...options,
        ...next,
        id,
      })
    },
  }
}

function dismiss(toastId?: string) {
  if (toastId) {
    sonnerToast.dismiss(toastId)
    return
  }
  sonnerToast.dismiss()
}

function useToast() {
  return React.useMemo(
    () => ({
      toasts: [] as Array<{ id: string }>,
      toast,
      dismiss,
    }),
    []
  )
}

export { dismiss, toast, useToast }
